import { action, computed, observable } from 'mobx';
import { setter, toggle } from 'mobx-decorators';
import get from 'lodash/get';
import PromptStore from 'prompt/src/stores/Prompt/PromptStore';
import constants from 'Constants';


// One store per mounted embed. It wraps the real PromptStore (so the whole
// block / form / submit / sync tree works unchanged) and adds the per-instance
// mount state (el, placement, render/retry bookkeeping). See prompt/inline.
class InlinePromptStore {

  @setter @observable id;
  @setter @observable el;
  @setter @toggle @observable rendered;
  @observable showPoweredBy;

  @setter @observable prompt;

  @setter @observable placement;
  @setter @observable placementCssSelector;
  @setter @observable placementInsertPosition;
  @setter @observable placementInsertRetries;

  constructor() {
    this.reset();
  }

  @action reset() {
    this.id = null;
    this.el = null;
    this.rendered = false;
    this.showPoweredBy = false;
    this.prompt = null;
    this.placement = "";
    this.placementCssSelector = "";
    this.placementInsertPosition = "";
    this.placementInsertRetries = 0;
  }

  @action async deserialize(data) {
    this.id = data.id;
    this.showPoweredBy = data.show_powered_by ? true : false;

    const placement = get(data, 'settings.placement') || {};
    this.placement = placement.mode || "";
    this.placementCssSelector = placement.css_selector || "";
    this.placementInsertPosition = placement.insert_position || "AFTER";

    // Build the real PromptStore from the payload. The backend keeps the
    // surface/layout invariant; the local assignment keeps already-cached
    // payloads safe during a rolling deploy.
    const prompt = new PromptStore();
    data.show_powered_by = this.showPoweredBy;
    await prompt.deserialize(data);
    prompt.setInline(true);
    prompt.restoreResponseSession();
    prompt.pages.forEach((page) => {
      if (page.content && page.content.setLayout) {
        page.content.setLayout(constants.PROMPT_CONTENT_LAYOUT_INLINE);
      }
    });

    // Resume a returning visitor's frozen game outcome. The popup does this in
    // trigger() (which the embed never calls — an embed is always shown, no display
    // timing), so the embed must invoke it here. Runs AFTER deserialize (pages loaded,
    // so getGameBlock() can read the Retake replay policy) and the caller loads the
    // visitor activity BEFORE store.deserialize, so getLatestPromptActivity sees it.
    // Without this the embed game is always fresh — no per-visitor lock, no cooldown.
    prompt.maybeResumeOutcome();
    prompt.finalizeIfTerminal();

    this.prompt = prompt;
  }

  @action incrPlacementInsertRetries() {
    this.placementInsertRetries += 1;
  }

  @computed get isSelectorPlacement() {
    return this.placement === "SELECTOR";
  }

  @computed get insertPositionBefore() {
    return this.placementInsertPosition === "BEFORE";
  }

  @computed get insertPositionAfter() {
    return this.placementInsertPosition === "AFTER";
  }

  @computed get insertPositionPrepend() {
    return this.placementInsertPosition === "PREPEND";
  }

  @computed get insertPositionAppend() {
    return this.placementInsertPosition === "APPEND";
  }
}

export default InlinePromptStore;
